import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import AddExceptionForm, { exceptionTypes } from "./AddExceptionForm";
import ExceptionList from "./ExceptionList";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Exception } from "../../../../../../../../../api/homeSiteExceptions/types";
import {
  useCreateNewNote,
  useNotesDataByHomesiteId,
  useUpdateNotes,
} from "../../../../../../../../../reactQuery/homeSites/notes/homeSiteNotes";
import {
  useCreateExceptions,
  useUpdateExceptions,
} from "../../../../../../../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { GridExpandMoreIcon } from "@mui/x-data-grid-premium";
import { DivisionMileStone } from "../../../../../../../../../api/divisionMileStones/types";
import { ALL_MILESTONES, STANDARD_HEIGHT_OF_DROPDOWN } from "../../../../../../../../../utils/constants";
import { thinDarkScrollbar } from "../../../../../../../../../utils/scrollbarStyles";

interface ExceptionComponentProps {
  userId: string;
  exceptionState: Exception[];
  homeSiteId: string;
  mileStones: DivisionMileStone[];
  divisionCode: string;
  conciergeEmail: string;
  opportunityId: string;
  exceptionName: string;
  setExceptionName: (name: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  exceptionMilestone: string;
  setExceptionMilestone: (ms: string) => void;
  exceptionType: exceptionTypes | null;
  setExceptionType: (t: exceptionTypes | null) => void;
  exceptionsDirty: boolean;
  setExceptionsDirty: (dirty: boolean) => void;
  setExceptionUpdateDirty?: React.Dispatch<React.SetStateAction<boolean>>;
  setExceptionUpdateHandler?: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const ExceptionComponent: React.FC<ExceptionComponentProps> = ({
  exceptionState,
  homeSiteId,
  userId,
  mileStones,
  divisionCode,
  conciergeEmail,
  opportunityId,
  exceptionName,
  setExceptionName,
  description,
  setDescription,
  exceptionMilestone,
  setExceptionMilestone,
  exceptionType,
  setExceptionType,
  exceptionsDirty,
  setExceptionsDirty,
  setExceptionUpdateDirty,
  setExceptionUpdateHandler,
  showSnackbar,
}) => {
  const { mutate: createException } = useCreateExceptions(
    divisionCode,
    homeSiteId
  );
  const { mutate: updateNote } = useUpdateNotes(divisionCode, homeSiteId);
  const { mutate: updateException } = useUpdateExceptions(
    divisionCode,
    homeSiteId
  );
  const { data: notesState } = useNotesDataByHomesiteId(
    divisionCode,
    homeSiteId
  );
  const { mutateAsync: createNote } = useCreateNewNote(
    divisionCode,
    homeSiteId
  );

  // Helper function to get valid milestone value for this component
  const getValidMilestoneValue = useCallback((milestone: string) => {
    if (milestone && mileStones.some(ms => ms.mileStoneName === milestone)) {
      return milestone;
    }
    return ALL_MILESTONES;
  }, [mileStones]);

  const selectedMilestone = useMemo(
    () => getValidMilestoneValue(exceptionMilestone),
    [exceptionMilestone, getValidMilestoneValue]
  );

  const [showResolved, setShowResolved] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);

  const ITEMS_PER_PAGE = 5;

  const toggleResolvedVisibility = () => setShowResolved((prev) => !prev);

  const handleMilestoneChange = (event: SelectChangeEvent<string>) => {
    setExceptionMilestone(event.target.value as string);
    setActivePage(1);
    setResolvedPage(1);
  };

  const processNotes = (status?: boolean) =>
    notesState?.data
      ?.filter(
        (note) =>
          (status === undefined || note.status === status) &&
          note.noteType === "exceptionNotes"
      )
      .reduce((acc, note) => {
        if (!acc[note.exceptionId]) {
          acc[note.exceptionId] = [];
        }
        acc[note.exceptionId].push(note);
        return acc;
      }, {} as Record<string, typeof notesState.data>);

  const handleAddClick = () => {
    setExceptionName("");
    setDescription("");
    setExceptionType(null);
    setShowAddForm(true);
  };

  const handeleSaveException = (
    exceptionName: string,
    description: string,
    mileStoneName: string,
    selectedType: exceptionTypes
  ) => {
    createException({
      description: description,
      homeSiteId: homeSiteId,
      mileStoneName: mileStoneName,
      divisionCode: divisionCode,
      conciergeEmail: conciergeEmail,
      exceptionType: selectedType,
      exceptionName: exceptionName,
      createdBy: userId,
    });
    setShowAddForm(false);
    showSnackbar("Exception added successfully!", "success");
  };

  const milestoneCounts = exceptionState
    ?.filter((exception) => exception.status === !showResolved)
    .reduce((acc, exception) => {
      acc[exception.mileStoneName] = (acc[exception.mileStoneName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const filterExceptions = (status: boolean) =>
    exceptionState?.filter(
      (exception) =>
        (selectedMilestone === ALL_MILESTONES ||
          exception.mileStoneName === selectedMilestone) &&
        exception.status === status
    );

  const activeExceptions = filterExceptions(true);
  const resolvedExceptions = filterExceptions(false);
  return (
    <>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          width: "504px",
          height: "480px",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          padding: 2,
          boxSizing: "border-box",
        }}
      >
        {!showAddForm ? (
          <>
            {showResolved && (
              <Box display="flex" alignItems="center" marginBottom={1}>
                <IconButton
                  edge="start"
                  sx={{ mr: 1 }}
                  onClick={() => setShowResolved(false)}
                >
                  <ArrowBackIcon sx={{ color: "#1C2B36" }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1C2B36",
                    fontWeight: "700px",
                    fontSize: "16px",
                  }}
                >
                  Resolved Exceptions
                </Typography>
              </Box>
            )}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={1}
            >
              <FormControl
                variant="standard"
                style={{ flexGrow: 1, marginRight: "15px" }}
              >
                {!showUpdateForm && (
                  <Select
                    IconComponent={(props) => (
                      <GridExpandMoreIcon
                        {...props}
                        sx={{
                          fontSize: 25,
                        }}
                      />
                    )}
                    labelId="milestone-select-label"
                    value={selectedMilestone}
                    onChange={handleMilestoneChange}
                    label="Milestone"
                    sx={{
                      fontWeight: "400",
                      "& .MuiSelect-icon": {
                        color: "#3E5463",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: STANDARD_HEIGHT_OF_DROPDOWN,
                          overflowY: "auto",
                          ...thinDarkScrollbar,
                        },
                      },
                    }}
                  >
                    <MenuItem value={ALL_MILESTONES}>
                      All Milestones (
                      <strong>
                        {
                          exceptionState?.filter(
                            (el) => el.status === !showResolved
                          ).length
                        }
                      </strong>
                      )
                    </MenuItem>
                    {mileStones.map((milestone) => (
                      <MenuItem
                        key={milestone.mileStoneName}
                        value={milestone.mileStoneName}
                      >
                        {milestone.mileStoneName} (
                        <strong>
                          {milestoneCounts[milestone.mileStoneName] || 0}
                        </strong>
                        )
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>

              {!showResolved && !showUpdateForm && (
                <Button
                  variant="contained"
                  onClick={handleAddClick}
                  sx={{
                    gap: "8px",
                    width: "100%",
                    maxWidth: "152px",
                    textAlign: "center",
                    textTransform: "none",
                  }}
                >
                  + Add Exception
                </Button>
              )}
            </Box>

            {!showResolved && (
              <ExceptionList
                exceptions={{
                  counts: filterExceptions(true).length,
                  data: filterExceptions(true),
                }}
                page={activePage}
                itemsPerPage={ITEMS_PER_PAGE}
                updateException={updateException}
                createNote={createNote}
                updateNote={updateNote}
                setActivePage={setActivePage}
                homeSiteId={homeSiteId}
                userId={userId}
                title="Active Exceptions"
                notes={processNotes(true) || {}}
                showResolved={showResolved}
                showUpdateForm={showUpdateForm}
                setShowUpdateForm={setShowUpdateForm}
                divisionCode={divisionCode}
                conciergeEmail={conciergeEmail}
                opportunityId={opportunityId}
                setExceptionsDirty={setExceptionsDirty}
                setExceptionUpdateDirty={setExceptionUpdateDirty}
                setExceptionUpdateHandler={setExceptionUpdateHandler}
                showSnackbar={showSnackbar}
              />
            )}
            {showResolved && (
              <ExceptionList
                exceptions={{
                  counts: filterExceptions(false).length,
                  data: filterExceptions(false),
                }}
                page={resolvedPage}
                itemsPerPage={ITEMS_PER_PAGE}
                updateException={updateException}
                createNote={createNote}
                updateNote={updateNote}
                setActivePage={setResolvedPage}
                homeSiteId={homeSiteId}
                userId={userId}
                title="Resolved Exceptions"
                notes={processNotes() || {}}
                showResolved={showResolved}
                showUpdateForm={showUpdateForm}
                setShowUpdateForm={setShowUpdateForm}
                divisionCode={divisionCode}
                conciergeEmail={conciergeEmail}
                opportunityId={opportunityId}
                setExceptionsDirty={setExceptionsDirty}
                setExceptionUpdateDirty={setExceptionUpdateDirty}
                setExceptionUpdateHandler={setExceptionUpdateHandler}
                showSnackbar={showSnackbar}
              />
            )}
          </>
        ) : (
          <AddExceptionForm
            milestones={mileStones?.map((milestone) => milestone.mileStoneName)}
            selectedMilestone={selectedMilestone}
            setSelectedMilestone={setExceptionMilestone}
            exceptionName={exceptionName}
            setExceptionName={setExceptionName}
            description={description}
            setDescription={setDescription}
            exceptionType={exceptionType}
            setExceptionType={setExceptionType}
            handleSaveException={(
              exceptionName,
              description,
              mileStoneName,
              selectedType
            ) =>
              handeleSaveException(
                exceptionName,
                description,
                mileStoneName,
                selectedType
              )
            }
            handleBack={() => setShowAddForm(false)}
            onExceptionDirtyChange={(dirty) => setExceptionsDirty(dirty)}
          />
        )}
      </Box>
      <Box style={{ backgroundColor: "white", borderRadius: "4px", height: "34px" }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          onClick={
            !showAddForm && !showUpdateForm ? toggleResolvedVisibility : undefined
          }
          sx={{
            cursor:
              !showAddForm && !showUpdateForm ? "pointer !important" : "default",
            width: "130px",
            backgroundColor: "transparent",
            marginLeft: "200px",
          }}
        >
          {!showAddForm && !showUpdateForm && (
            <>
              {showResolved
                ? activeExceptions.length > 0 && (
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#005DAA",
                      fontWeight: "700",
                      fontSize: "12px",
                    }}
                  >
                    Show Active ({activeExceptions.length})
                  </Typography>
                )
                : resolvedExceptions.length > 0 && (
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#005DAA",
                      fontWeight: "700",
                      fontSize: "12px",
                    }}
                  >
                    Show Resolved ({resolvedExceptions.length})
                  </Typography>
                )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ExceptionComponent;